/**
 * Dashboard Service
 *
 * Fetches statistics from Google Sheets for the dashboard:
 * - Calendar events (visiting bookings)
 * - Find by request form submissions
 * - Property counts by district
 *
 * Includes sessionStorage caching to provide instant data on navigation
 * and background refresh on page reload.
 */

const CALENDAR_API = '/api/calendar-events';
const FORM_API = '/api/google-sheet?type=form-submissions';

// --- Cache Helpers ---
const CACHE_PREFIX = 'dash_';
const CACHE_TTL_MS = 120_000; // 2 minutes

function getCacheKey(key) {
  return `${CACHE_PREFIX}${key}`;
}

function getCached(key) {
  try {
    const raw = sessionStorage.getItem(getCacheKey(key));
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(getCacheKey(key));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCache(key, data) {
  try {
    sessionStorage.setItem(
      getCacheKey(key),
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // storage full or unavailable – silently skip
  }
}

function clearCache() {
  try {
    const keys = Object.keys(sessionStorage).filter((k) =>
      k.startsWith(CACHE_PREFIX)
    );
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // skip
  }
}

/**
 * Get date range for filtering
 */
const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (period) {
    case 'day':
      return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
    case 'week': {
      const dayOfWeek = today.getDay();
      const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const weekStart = new Date(today.setDate(diff));
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return { start: weekStart, end: weekEnd };
    }
    case 'month': {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return { start: monthStart, end: monthEnd };
    }
    default:
      return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
  }
};

/**
 * Filter events by date range
 */
const filterByDateRange = (events, start, end) => {
  return events.filter((event) => {
    const eventDate = new Date(event.date);
    return eventDate >= start && eventDate < end;
  });
};

/**
 * Load calendar events
 */
export async function loadCalendarEvents() {
  try {
    const response = await fetch(CALENDAR_API);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.events) ? data.events : [];
    }
  } catch (error) {
    console.error('[Dashboard] Error loading calendar events:', error);
  }
  return [];
}

/**
 * Load form submissions (find by request)
 */
export async function loadFormSubmissions() {
  try {
    const response = await fetch(FORM_API);
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.submissions) ? data.submissions : [];
    }
  } catch (error) {
    console.error('[Dashboard] Error loading form submissions:', error);
  }
  return [];
}

/**
 * Load all calendar events with details
 */
export async function loadAllCalendarEvents() {
  return loadCalendarEvents();
}

/**
 * Load all form submissions with details
 */
export async function loadAllFormSubmissions() {
  return loadFormSubmissions();
}

/**
 * Load property counts by district from Google Drive
 */
export async function loadPropertyCountsByDistrict() {
  try {
    const response = await fetch('/api/drive-properties');
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data.properties)) {
        const districtCounts = {};
        data.properties.forEach((property) => {
          const district = property.district || 'Khác';
          districtCounts[district] = (districtCounts[district] || 0) + 1;
        });
        return districtCounts;
      }
    }
  } catch (error) {
    console.error('[Dashboard] Error loading property counts:', error);
  }
  return {};
}

/**
 * Get statistics for visiting bookings
 */
export async function getVisitingBookingStats(period = 'day') {
  const events = await loadCalendarEvents();
  const { start, end } = getDateRange(period);
  const periodEvents = filterByDateRange(events, start, end);

  const stats = {
    new: periodEvents.filter((e) => !e.isFinalized && !e.isRevisit).length,
    done: periodEvents.filter((e) => e.isFinalized).length,
    pending: periodEvents.filter((e) => e.isRevisit).length,
  };

  return stats;
}

/**
 * Get statistics for find by requests
 */
export async function getFindByRequestStats(period = 'day') {
  const submissions = await loadFormSubmissions();
  const { start, end } = getDateRange(period);
  
  // Form submissions should have a timestamp field
  const periodSubmissions = submissions.filter((submission) => {
    if (!submission.timestamp) return true; // If no timestamp, include all
    const submissionDate = new Date(submission.timestamp);
    return submissionDate >= start && submissionDate < end;
  });

  return {
    count: periodSubmissions.length,
  };
}

/**
 * Get managed properties statistics
 */
export async function getManagedPropertiesStats() {
  const districtCounts = await loadPropertyCountsByDistrict();
  const total = Object.values(districtCounts).reduce((sum, count) => sum + count, 0);

  return {
    total,
    byDistrict: districtCounts,
  };
}

/**
 * Get all dashboard statistics (cached).
 *
 * Returns cached data instantly when available (≤ TTL).
 * Always performs a background refresh and updates the cache
 * so subsequent reads are fresh.
 *
 * @param {string} period - 'day' | 'week' | 'month'
 * @param {boolean} forceRefresh - skip cache and fetch fresh data
 * @returns {Promise<object>} stats object
 */
export async function getDashboardStats(period = 'day', forceRefresh = false) {
  const cacheKey = `stats_${period}`;

  // Return cached data immediately if available and not forcing refresh
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) {
      // Kick off background refresh without awaiting – fresh data will
      // be stored in cache for the next read.
      refreshStats(period, cacheKey);
      return cached;
    }
  }

  // Fresh fetch
  const [visitingStats, findByRequestStats, propertiesStats] = await Promise.all([
    getVisitingBookingStats(period),
    getFindByRequestStats(period),
    getManagedPropertiesStats(),
  ]);

  const stats = {
    visiting: visitingStats,
    findByRequest: findByRequestStats,
    properties: propertiesStats,
    period,
  };

  setCache(cacheKey, stats);
  return stats;
}

/**
 * Background refresh – fetches fresh data and updates cache silently.
 */
async function refreshStats(period, cacheKey) {
  try {
    const [visitingStats, findByRequestStats, propertiesStats] = await Promise.all([
      getVisitingBookingStats(period),
      getFindByRequestStats(period),
      getManagedPropertiesStats(),
    ]);

    setCache(cacheKey, {
      visiting: visitingStats,
      findByRequest: findByRequestStats,
      properties: propertiesStats,
      period,
    });
  } catch {
    // Silent – stale cache is better than an error
  }
}

export { clearCache, getCached, setCache };

const DashboardService = {
  getVisitingBookingStats,
  getFindByRequestStats,
  getManagedPropertiesStats,
  getDashboardStats,
  clearCache,
};

export default DashboardService;
